import { Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

type TimeSelectProps = {
  value: string; // "HH:mm" 24h
  onChange: (value: string) => void;
  disabled?: boolean;
};

function parse(value: string) {
  const [hRaw, mRaw] = value.split(":").map(Number);
  const h24 = Number.isFinite(hRaw) ? hRaw : 9;
  const m = Number.isFinite(mRaw) ? (Math.round(mRaw / 5) * 5) % 60 : 0;
  const period: "AM" | "PM" = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { h12, m, period };
}

function formatLabel(h12: number, m: number, period: "AM" | "PM") {
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export function TimeSelect({ value, onChange, disabled }: TimeSelectProps) {
  const { h12, m, period } = parse(value);

  function emit(nextH12: number, nextM: number, nextPeriod: "AM" | "PM") {
    let h = nextH12 % 12;
    if (nextPeriod === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${String(nextM).padStart(2, "0")}`);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="border-input focus-visible:ring-ring/50 flex h-9 w-32 shrink-0 cursor-pointer items-center justify-between gap-2 rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {formatLabel(h12, m, period)}
          <Clock className="text-muted-foreground size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <div className="flex gap-1">
          <div className="flex max-h-48 w-14 flex-col gap-0.5 overflow-y-auto">
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                ref={(el) => {
                  if (h === h12) el?.scrollIntoView({ block: "center" });
                }}
                onClick={() => emit(h, m, period)}
                className={cn(
                  "hover:bg-accent cursor-pointer rounded px-2 py-1 text-sm",
                  h === h12 && "bg-primary text-primary-foreground hover:bg-primary"
                )}
              >
                {h}
              </button>
            ))}
          </div>
          <div className="flex max-h-48 w-14 flex-col gap-0.5 overflow-y-auto">
            {MINUTES.map((mm) => (
              <button
                key={mm}
                type="button"
                ref={(el) => {
                  if (mm === m) el?.scrollIntoView({ block: "center" });
                }}
                onClick={() => emit(h12, mm, period)}
                className={cn(
                  "hover:bg-accent cursor-pointer rounded px-2 py-1 text-sm",
                  mm === m && "bg-primary text-primary-foreground hover:bg-primary"
                )}
              >
                {String(mm).padStart(2, "0")}
              </button>
            ))}
          </div>
          <div className="flex w-14 flex-col gap-0.5">
            {(["AM", "PM"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => emit(h12, m, p)}
                className={cn(
                  "hover:bg-accent cursor-pointer rounded px-2 py-1 text-sm",
                  p === period && "bg-primary text-primary-foreground hover:bg-primary"
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
